import ky from 'ky';
import { IPublicClientApplication } from '@azure/msal-browser';
import { InteractionRequiredAuthError } from '@azure/msal-common';
import { HttpHeader, MimeValueType } from '@cs-cms/types';

export interface AuthScopes {
  apiScopes: string[];
  graphScopes: string[];
}

class KyClientFactory {
  private msalInstance: IPublicClientApplication | null = null;
  private scopes: AuthScopes | null = null;

  initialize(msalInstance: IPublicClientApplication, scopes: AuthScopes): void {
    this.msalInstance = msalInstance;
    this.scopes = scopes;
  }

  private getTokenResponse = async (isMsGraphEndpoint: boolean) => {
    if (!this.msalInstance || !this.scopes) {
      throw new Error('KyClientFactory not initialized. Call initialize() with msalInstance and scopes first.');
    }

    const msalInstance = this.msalInstance;
    const scopes = this.scopes;

    const activeAccount = msalInstance.getActiveAccount();
    const accounts = msalInstance.getAllAccounts();

    if (!activeAccount && accounts.length === 0) {
      throw new Error('User is not signed in.');
    }

    const requestScopes = isMsGraphEndpoint ? scopes.graphScopes : scopes.apiScopes;

    const silentRequest = {
      account: activeAccount || accounts[0],
      scopes: requestScopes
    };

    return msalInstance.acquireTokenSilent(silentRequest).catch(async error => {
      if (error instanceof InteractionRequiredAuthError) {
        return msalInstance.acquireTokenRedirect(silentRequest);
      }
      throw error;
    });
  };

  private setDefaultHeaders = (options: Request) => {
    const hasContentType = options.headers.has(HttpHeader.ContentType);
    const hasAccept = options.headers.has(HttpHeader.Accept);

    if (!hasContentType) {
      options.headers.set(HttpHeader.ContentType, MimeValueType.Json);
    }
    if (!hasAccept) {
      options.headers.set(HttpHeader.Accept, MimeValueType.Json);
    }
  };

  private addAuthToken = async (request: Request, isMsGraphEndpoint: boolean) => {
    try {
      const tokenResponse = await this.getTokenResponse(isMsGraphEndpoint);
      if (tokenResponse) {
        request.headers.set(HttpHeader.Authorization, `Bearer ${tokenResponse.accessToken}`);
      }
    } catch (error) {
      console.error('Error adding auth token:', error);
      throw error;
    }
  };

  private handleUnauthorizedResponse = async (request: Request, response: Response, isMsGraphEndpoint: boolean) => {
    if (response.status !== 401) {
      return response;
    }

    const tokenResponse = await this.getTokenResponse(isMsGraphEndpoint);
    if (!tokenResponse) {
      return response;
    }

    const token = isMsGraphEndpoint ? tokenResponse.accessToken : tokenResponse.idToken;
    request.headers.set(HttpHeader.Authorization, `Bearer ${token}`);
    return ky(request);
  };

  createKyClient = (isMsGraphEndpoint: boolean, apiUrl: string, timeout?: number) => {
    if (!this.msalInstance) {
      throw new Error('KyClientFactory not initialized. Call initialize() with msalInstance and scopes first.');
    }

    return ky.extend({
      timeout: timeout ?? 100000,
      retry: 3,
      hooks: {
        beforeRequest: [
          options => {
            if (options.method !== 'GET') {
              this.setDefaultHeaders(options);
            }
          },
          async request => this.addAuthToken(request, isMsGraphEndpoint)
        ],
        afterResponse: [
          async (request, options, response) => this.handleUnauthorizedResponse(request, response, isMsGraphEndpoint)
        ]
      },
      prefixUrl: isMsGraphEndpoint ? undefined : apiUrl
    });
  };
}

export const kyClientFactory = new KyClientFactory();
